// src/features/users/components/UserList.tsx

import React from 'react';
import {
  List,
  FunctionField,
  useTranslate,
} from 'react-admin';
import { Box, Typography, Chip } from '@mui/material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import UserActions from './UserActions';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { User } from '../types';
import CustomListToolbar from '@/components/common/CustomListToolbar';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';


const UserFilters = () => {
  const translate = useTranslate();
  return [
    <CustomTextInputFilter 
      key="search" 
      source="q" 
      label="resources.users.filters.search_placeholder" 
      alwaysOn 
    />,
    <CustomSelectInputFilter 
      key="role" 
      source="role" 
      label="resources.users.fields.role"
      choices={[
        { id: 'SuperAdmin', name: translate('resources.users.roles.super_admin') },
        { id: 'GroupAdmin', name: translate('resources.users.roles.group_admin') },
        { id: 'HotelAdmin', name: translate('resources.users.roles.hotel_admin') },
        { id: 'Receptionist', name: translate('resources.users.roles.receptionist') },
      ]} 
      alwaysOn 
    />,
  ];
};

export const UserList: React.FC = (props) => {
  const translate = useTranslate();
  const { canViewUsers } = useUserPermissions();

  if (!canViewUsers) {
    return <Typography color="error">{translate('resources.users.messages.access_denied')}</Typography>;
  }

  return (
    <List
      {...props}
      title={translate('resources.users.titles.list')}
      actions={<CustomListToolbar
          showFilterButton={false}
          showCreate={canViewUsers}
          createLabel={translate('resources.users.actions.create', 'Nuevo Usuario')}
        />}
      filters={UserFilters()}
      perPage={25}
      sort={{ field: 'name', order: 'ASC' }}
    >
      <CustomDataGrid rowClick="edit">
        <FunctionField
          label=""
          render={(record: User) => <UserActions record={record} />}
          sx={{ width: '4em' }}
        />
        <FunctionField
          source="name"
          label={translate('resources.users.fields.full_name')}
          render={(record: User) => <Typography variant="body2">{record.name}</Typography>}
        />
        <FunctionField
          source="email"
          label={translate('resources.users.fields.email')}
          render={(record: User) => <Typography variant="body2">{record.email}</Typography>}
        />
        <FunctionField
          label={translate('resources.users.fields.role_assignment')}
          render={(record: User) => (
            <Box>
              <Chip 
                label={record.role} 
                size="small"
                color={
                    record.role === 'SuperAdmin' ? 'secondary' :
                    record.role === 'GroupAdmin' ? 'primary' :
                    record.role === 'HotelAdmin' ? 'info' : 'default'
                }
              />
            </Box>
          )}
        />

        <FunctionField
          label={translate('resources.users.fields.group')}
          render={(record: User) => (
              <Typography variant="body2">
               {record.group?.name}
               </Typography>
          )}
        />

        <FunctionField
          label={translate('resources.users.fields.hotel')}
          render={(record: User) => (
              <Typography variant="body2">
               {record.hotel?.name}
              </Typography>
          )}
        />
      </CustomDataGrid>
    </List>
  );
};

export default UserList;